import type { ApolloClient, NormalizedCacheObject } from '@apollo/client'
import {
  type AppPropsWithFeatureFlags,
  withFeatureFlags,
} from '@jusbrasil-web/shared-feature-flags'
import type { GetServerSidePropsContext } from 'next'

import { isSignupModalExperimentEligible } from './eligibility'
import {
  type SignupModalFlags,
  type SignupModalVariant,
  isSignupModalVariant,
  SIGNUP_MODAL_FLAG_KEY,
  SIGNUP_MODAL_FLAGS,
} from './flag'
import { type SignupModalSegment, deriveSignupModalSegment } from './segment'

const FORCE_QUERY_PARAM = 'force_signup_modal'

function readForcedVariant(ctx: GetServerSidePropsContext): SignupModalVariant | null {
  if (process.env.NEXT_PUBLIC_ENV === 'production') return null
  const raw = ctx.query?.[FORCE_QUERY_PARAM]
  const value = Array.isArray(raw) ? raw[0] : raw
  return isSignupModalVariant(value) ? value : null
}

export interface ResolveVariantInput {
  ctx: GetServerSidePropsContext
  apolloClient: ApolloClient<NormalizedCacheObject>
  referer: string | null | undefined
}

export interface ResolveVariantResult {
  forcedVariant: SignupModalVariant | null
  segment: SignupModalSegment
  eligible: boolean
  inExperiment: boolean
  featureFlagsProps: AppPropsWithFeatureFlags<SignupModalFlags>
}

async function resolveFlag(
  ctx: GetServerSidePropsContext,
  apolloClient: ApolloClient<NormalizedCacheObject>
): Promise<Pick<ResolveVariantResult, 'featureFlagsProps' | 'inExperiment'>> {
  const ff = await withFeatureFlags(ctx, apolloClient, SIGNUP_MODAL_FLAGS)
  return {
    featureFlagsProps: ff.withFeatureFlagsProps(),
    inExperiment: ff.inExperiment(SIGNUP_MODAL_FLAG_KEY),
  }
}

export async function resolveSignupModalVariant({
  ctx,
  apolloClient,
  referer,
}: ResolveVariantInput): Promise<ResolveVariantResult> {
  const forcedVariant = readForcedVariant(ctx)
  const segment = deriveSignupModalSegment({ referer })
  const flag = await resolveFlag(ctx, apolloClient)

  if (forcedVariant) {
    return {
      forcedVariant,
      segment: segment === 'person' || segment === 'lawsuit' ? segment : 'person',
      eligible: true,
      inExperiment: false,
      featureFlagsProps: flag.featureFlagsProps,
    }
  }

  return {
    forcedVariant: null,
    segment,
    eligible: isSignupModalExperimentEligible(ctx.query, segment),
    inExperiment: flag.inExperiment,
    featureFlagsProps: flag.featureFlagsProps,
  }
}
